import { AnimatePresence, motion } from "framer-motion";
import type { Faq } from "@/data/faqs";
import { useState } from "react";

import { easeOut } from "@/lib/motion";

/**
 * FAQ accordion.
 *
 * Native <button> per row with `aria-expanded` and `aria-controls`; the answer
 * region is labelled by its trigger. Answers stay in the DOM when collapsed, so
 * the FAQPage structured data always describes content that is really on the page.
 */
export function Accordion({ items }: { items: Faq[] }) {
  const [open, setOpen] = useState<number | null>(0);

  return (
    <div className="border-t border-line">
      {items.map((item, i) => {
        const expanded = open === i;
        return (
          <div key={item.question} className="border-b border-line">
            <h3>
              <button
                type="button"
                id={`faq-trigger-${i}`}
                aria-expanded={expanded}
                aria-controls={`faq-panel-${i}`}
                onClick={() => setOpen(expanded ? null : i)}
                className="group flex w-full items-start justify-between gap-8 py-7 text-left"
              >
                <span className="font-serif text-[1.125rem] text-midnight transition-colors duration-300 group-hover:text-navy md:text-[1.25rem]">
                  {item.question}
                </span>
                <span
                  aria-hidden="true"
                  className="relative mt-1.5 h-4 w-4 shrink-0 text-blue"
                >
                  <span className="absolute left-0 top-1/2 h-px w-4 -translate-y-1/2 bg-current" />
                  <span
                    className={`absolute left-1/2 top-0 h-4 w-px -translate-x-1/2 bg-current transition-transform duration-400 ease-[cubic-bezier(0.16,1,0.3,1)] ${
                      expanded ? "scale-y-0" : "scale-y-100"
                    }`}
                  />
                </span>
              </button>
            </h3>

            <AnimatePresence initial={false}>
              {expanded ? (
                <motion.div
                  key="panel"
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.4, ease: easeOut }}
                  className="overflow-hidden"
                >
                  <div
                    id={`faq-panel-${i}`}
                    role="region"
                    aria-labelledby={`faq-trigger-${i}`}
                    className="max-w-2xl pb-8 pr-10 text-muted"
                  >
                    {item.answer}
                  </div>
                </motion.div>
              ) : (
                // Kept in the DOM (visually hidden) so the answer text is always present.
                <div
                  id={`faq-panel-${i}`}
                  role="region"
                  aria-labelledby={`faq-trigger-${i}`}
                  hidden
                >
                  {item.answer}
                </div>
              )}
            </AnimatePresence>
          </div>
        );
      })}
    </div>
  );
}
